// @flow
import ApiService from '@api/service';
import EventEmitter from '_common/services/EventEmitter';

import type { Listener, Error } from '@core/types';
import { API_PATHS } from '@app/constants/paths';
import type { InterventionType, InterventionPayload } from '@root/src/Intervention/types';
import DateService from '@common/services/DateService';

const { GROUP_CLASS_INTERVENTIONS } = API_PATHS;

type UpdateValues = (interventions: InterventionType[], isLoading: boolean, errors?: Error[]) => void;
type FetchAll = (groupClassId: number, language: string) => Promise<InterventionType[]>;
type OnChange = (listener: Listener) => Function;
type Trigger = () => void;

const parseToDate = (value: string): Date => new Date(value);
const parseIntervention = (
  payload: InterventionPayload,
): InterventionType => {
  const startDate = parseToDate(payload.startDate);
  const endDate = parseToDate(payload.endDate);

  return {
    id: Number(payload.id),
    startDate,
    endDate,
    name: DateService.localDateLongDisplay(startDate), // we don't have name, so we use formatted date as name
  };
};

let sourceAll = ApiService.createToken();

class InterventionsService {
  constructor() {
    this.eventEmitter = new EventEmitter();
    this.interventions = [];
    this.isLoading = false;
    this.errors = [];
  }

  eventEmitter: EventEmitter;
  interventions: InterventionType[];
  isLoading: boolean;
  errors: Error[];

  get interventionsValue(): InterventionType[] {
    return this.interventions;
  }

  get isLoadingValue(): boolean {
    return this.isLoading;
  }

  get errorsValue(): Error[] {
    return this.errors;
  }

  updateValues: UpdateValues = (interventions: InterventionType[], isLoading: boolean, errors: Error[] = []): void => {
    this.interventions = interventions;
    this.isLoading = isLoading;
    this.errors = errors;
    this.#trigger();
  };

  fetchAll: FetchAll = (groupClassId: number, language: string): Promise<InterventionType[]> => {
    sourceAll.cancel();
    sourceAll = ApiService.createToken();

    this.updateValues(this.interventions, true, this.errors);

    return ApiService.request({
      method: 'get',
      url: GROUP_CLASS_INTERVENTIONS.replace(':id', String(groupClassId)),
      cancelToken: sourceAll.token,
      headers: {
        'Accept-Language': language,
      },
    })
      .then((payload) => {
        if (payload.data) {
          const rawData = Array.isArray(payload.data) ? payload.data : payload.data['hydra:member'] || [];
          const interventions = rawData.map((intervention: InterventionPayload, index: number) => (
            parseIntervention(intervention, index)
          ));
          this.updateValues(interventions, false);
          return interventions;
        }
        this.updateValues(this.interventions, false, this.errors);
        return Promise.reject();
      })
      .catch((error) => {
        this.updateValues(this.interventions, false, [...this.errors, { error }]);
        return Promise.reject();
      });
  };

  onChange: OnChange = (listener: Listener): Function => {
    const listenerFunction = this.eventEmitter.addListener(listener);
    this.#trigger();
    return listenerFunction;
  };

  /**
   * @private
   */
  #trigger: Trigger = (): void => {
    this.eventEmitter.trigger({
      interventions: this.interventions,
      isLoading: this.isLoading,
      errors: this.errors,
    });
  };
}

const instance: InterventionsService = new InterventionsService();
export default instance;
